// Cards for what stands on its own, tiles for a number at a glance, facts as a list of what's what, and disclosures
// that open to say more.
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import s from './Card.module.css';
import { cx } from './cx';
import { CaretRightIcon } from './icons';

export function Card({
  title,
  subtitle,
  actions,
  level = 2,
  flush = false,
  children,
  className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Beside the title. */
  actions?: ReactNode;
  level?: 2 | 3;
  /** No padding: for a list or a table to fill it. */
  flush?: boolean;
  children?: ReactNode;
  className?: string;
}) {
  const id = useId();
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <section className={cx(s.card, flush && s.flush, className)} aria-labelledby={title ? id : undefined}>
      {(title || actions) && (
        <div className={s.cardHead}>
          <div>
            {title && (
              <Heading id={id} className={s.cardTitle}>
                {title}
              </Heading>
            )}
            {subtitle && <p className={s.cardSubtitle}>{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** A card that's a link as a whole. Its first heading names where it goes. */
export function CardLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cx(s.card, className)}>
      {children}
    </Link>
  );
}

/** A number at a glance: what it counts, the number, and a line on it. */
export function StatTile({
  label,
  value,
  detail,
  icon,
  to,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  icon?: ReactNode;
  to?: string;
}) {
  const inside = (
    <>
      <span className={s.tileLabel}>
        {icon && <span aria-hidden="true">{icon}</span>}
        {label}
      </span>
      <span className={s.tileValue}>{value}</span>
      {detail && <span className={s.tileDetail}>{detail}</span>}
    </>
  );
  return to ? (
    <Link to={to} className={s.tile}>
      {inside}
    </Link>
  ) : (
    <div className={s.tile}>{inside}</div>
  );
}

/** Tiles in a grid that fits the width. */
export function StatTiles({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className={s.tiles} role={label ? 'group' : undefined} aria-label={label}>
      {children}
    </div>
  );
}

/** What's what: terms and their values, side by side, or one above the other on a phone. */
export function Facts({ items }: { items: ReadonlyArray<readonly [ReactNode, ReactNode] | null | false> }) {
  return (
    <dl className={s.facts}>
      {items
        .filter((item): item is readonly [ReactNode, ReactNode] => Boolean(item))
        .map(([term, value], i) => (
          <div key={i} className={s.fact}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
    </dl>
  );
}

/** A heading that opens to say more. */
export function Disclosure({
  summary,
  children,
  open,
  onToggle,
}: {
  summary: ReactNode;
  children: ReactNode;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}) {
  return (
    <details
      className={s.disclosure}
      open={open}
      onToggle={onToggle ? (e) => onToggle((e.currentTarget as HTMLDetailsElement).open) : undefined}
    >
      <summary>
        <span>{summary}</span>
        <CaretRightIcon className={s.caret} size={16} weight="bold" aria-hidden="true" />
      </summary>
      <div className={s.disclosureBody}>{children}</div>
    </details>
  );
}
